import { ApiResponse, create } from "apisauce";
import isElectron from "is-electron";
import { BookmarkedLeaf, IMinimalApi, ReadLaterLeaf, SearchLeaf, TDocument, TDocumentBase, TDocumentTree, Themes, TLeaf } from "../types";

// let token = localStorage.getItem("token");
const url = (window as any)._env_.REACT_APP_SERVER_URL || "http://localhost:50002";
// const url = 'https://program-legislatie.ro'

const headers: Record<string, any> = { Accept: "application/json", isLegacy: isElectron() ? "true" : undefined };

// console.log(isElectron(), headers);
const api = create({
  baseURL: url,
  headers,
  withCredentials: true,
});

const refreshApi = create({
  baseURL: url,
  headers,
  withCredentials: true,
});

const origin =
  window.location.protocol === "file:"
    ? `?origin=${encodeURIComponent(window.location.origin + window.location.pathname + "#")}`
    : isElectron()
    ? `?origin=${encodeURIComponent(window.location.origin + window.location.pathname + "#")}`
    : "";

api.axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = {
      ...config.headers,
      token,
    };
  }
  return config;
});

api.axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error(error);
    const originalRequest = error.config;
    const token = localStorage.getItem("token");
    if (error.response.status === 401 && !originalRequest._retry) {
      // try refreshing the token
      if (token) {
        const { ok, status, headers, originalError } = await refreshApi.get(
          "/api/refresh",
          {},
          {
            headers: {
              token,
            },
          }
        );
        if (ok && headers?.token) {
          localStorage.setItem("token", token);
          originalRequest._retry = true;
          originalRequest.headers.token = token;
          return api.axiosInstance(originalRequest);
        } else {
          console.error("Refresh failed", status, originalError);
        }
      }
    }
    throw error;
  }
);

const apiResponseHandler = <T, U>({ status, data, ok, problem, originalError }: ApiResponse<T, U>, emptyResponse?: T) => {
  if (ok) {
    return data || emptyResponse;
  } else {
    switch (status) {
      case 404:
        return emptyResponse;
      case 406:
      case 401:
      case 403:
        localStorage.removeItem('token')
        return emptyResponse;
      default:
        const errorMessage = data && (data as any).error ? (data as any).error.message || (data as any).error : problem;
        console.error(errorMessage);
        return emptyResponse;
    }
  }
};

export const newApiService: IMinimalApi = {
  // index
  loadIndexByYearAndTypes: () =>
    api.get<TDocumentTree>("/api/private/docs/index-years-types").then((res) => apiResponseHandler(res, []) || []),
  loadDocTitlesForYearAndType: (year: number, type: string, count: number) =>
    api.post<TLeaf[]>("/api/private/docs/index-years-types-docs", { year, type, count }).then((res) => apiResponseHandler(res, []) || []),

  // index pe monitoare
  loadIndexByYearAndMonitorNumber: () =>
    api.get<TDocumentTree>("/api/private/docs/index-years-mons").then((res) => apiResponseHandler(res, []) || []),
  loadDocTitlesForYearAndMon: (year: number, mon_nr: number, date: string, count: number) =>
    api
      .post<TLeaf[]>("/api/private/docs/index-years-mons-docs", { year, mon_nr, date, count })
      .then((res) => apiResponseHandler(res, []) || []),

  // read later
  loadReadLaterDocs: () => api.get<ReadLaterLeaf[]>(`/api/private/read-later/docs`).then((res) => apiResponseHandler(res, []) || []),
  addReadLaterDoc: (docid: string) => api.put<void>(`/api/private/read-later/add-doc`, { docid }).then(apiResponseHandler),
  removeReadLaterDoc: (docid: string) => api.delete<void>(`/api/private/read-later/docs`, { docid }).then(apiResponseHandler),

  // bookmarks | favorite
  loadBookmarkedDocs: () =>
    api.get<BookmarkedLeaf[]>(`/api/private/bookmarks/tracked-docs`).then((res) => apiResponseHandler(res, []) || []),
  addToBookmarks: (docid: string) =>
    api.put<void>(`/api/private/bookmarks/start-tracking-doc/${docid}`).then(apiResponseHandler),
  removeFromBookmarks: (docid: string) =>
    api.put<void>(`/api/private/bookmarks/stop-tracking-doc/${docid}`).then(apiResponseHandler),

  loadDocumentById: (id: string, useLatestRevisionOnLinks = false) =>
    api
      .get<TDocument>(
        `/api/private/docs/doc/${encodeURIComponent(id)}/${useLatestRevisionOnLinks ? "true" : "false"}${origin}${
          origin.startsWith("file://") ? "&rerouteimgs=false" : ""
        }`
      )
      .then(apiResponseHandler),

  loadDocumentByLexId: (lexid: string) =>
    api
      .get<TDocument>(
        `/api/private/docs/doclex/${encodeURIComponent(lexid)}${origin}${origin.startsWith("file://") ? "&rerouteimgs=false" : ""}`
      )
      .then(apiResponseHandler),

  getLatestDocs: () => api.get<TDocumentTree>(`/api/private/docs/index-last-mons/10`).then((res) => apiResponseHandler(res, []) || []),

  search: async (filters: any, start?: number, size?: number, doAggs = true, includeHtmlAndMarkdown = false) => {
    if (filters.isCaseSearch) {
      const result = await api.post<{ results: SearchLeaf[] }>("/api/private/cases/search-cases", { ...filters });
      return apiResponseHandler(result, { results: [] })?.results || [];
    } else {
      const result = await api.post<{ results: SearchLeaf[] }>("/api/private/docs/search", {
        filters,
        start,
        size,
        doAggs,
        includeHtmlAndMarkdown,
      });
      return apiResponseHandler(result, { results: [] })?.results || [];
    }
  },

  updateUserTheme: async (theme: Themes) => await api.post("/api/private/changetheme", { theme }),

  docxgenForDoc: (id: string, hideReds = false) =>
    api.get(
      `/api/private/docs/docx/${id}${hideReds ? (origin ? origin + "&hidereds=1" : "?hidereds=1") : origin}`,
      {},
      { responseType: 'arraybuffer' } as any,
    ).then((res) => res.data as ArrayBuffer | undefined),

  pdfgenForDoc: (id: string, hideReds = false) =>
    api.get(
      `/api/private/docs/pdfdoc/${id}${hideReds ? (origin ? origin + "&hidereds=1" : "?hidereds=1") : origin}`,
      {},
      { responseType: 'arraybuffer' } as any,
    ).then((res) => res.data as ArrayBuffer | undefined),

  docxgenForHTML: (html: string) =>
    api.post(
      "/api/private/docs/docxhtml",
      html,
      { headers: { "Content-type": "text/html" }, responseType: "arraybuffer" } as any,
    ).then((res) => res.data as ArrayBuffer | undefined),

  pdfgenForHTML: (html: string) =>
    api.post(
      "/api/private/docs/pdfhtml",
      html,
      { headers: { "Content-type": "text/html" }, responseType: "arraybuffer" } as any,
    ).then((res) => res.data as ArrayBuffer | undefined),
};

const mockDocuments: TDocumentBase[] = [
  { id: "1", title: "Project Proposal" },
  { id: "2", title: "Meeting Notes" },
  { id: "3", title: "Budget Report" },
  { id: "4", title: "Marketing Plan" },
  { id: "5", title: "Technical Specs" },
];
// Temporary
export const getDocuments = async (): Promise<TDocumentBase[]> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));
  return mockDocuments;
};
